import React, { useState, useEffect } from 'react';
import { Plus, Trash2, TrendingUp, DollarSign, Calculator } from 'lucide-react';
import { toast } from 'sonner';

export const CostingModule = ({ initialCosting, onSave }) => {
  const [venta, setVenta] = useState({
    precio_venta_total: 0,
    tipo_venta: '',
    descuento: 0,
    valor_final: 0,
    forma_pago: '',
    anticipo: 0,
    saldo_pendiente: 0
  });

  const [produccion, setProduccion] = useState([]);

  useEffect(() => {
    if (initialCosting) {
      if (initialCosting.venta) {
        setVenta(initialCosting.venta);
      }
      if (initialCosting.produccion) {
        setProduccion(initialCosting.produccion);
      }
    }
  }, [initialCosting]);

  useEffect(() => {
    const valorFinal = venta.precio_venta_total - venta.descuento;
    const saldoPendiente = valorFinal - venta.anticipo;
    
    setVenta(prev => ({
      ...prev,
      valor_final: valorFinal,
      saldo_pendiente: saldoPendiente
    }));
  }, [venta.precio_venta_total, venta.descuento, venta.anticipo]);

  const agregarItem = () => {
    const newItem = {
      id: Date.now().toString(),
      concepto: '',
      proveedor: '',
      cantidad: 1,
      costo_unitario: 0,
      costo_total: 0
    };
    setProduccion([...produccion, newItem]);
  };

  const eliminarItem = (id) => {
    setProduccion(produccion.filter(item => item.id !== id));
  };

  const actualizarItem = (id, field, value) => {
    setProduccion(produccion.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        
        if (field === 'cantidad' || field === 'costo_unitario') {
          updated.costo_total = updated.cantidad * updated.costo_unitario;
        }
        
        return updated;
      }
      return item;
    }));
  };

  const calculos = {
    costoTotalProduccion: produccion.reduce((sum, item) => sum + item.costo_total, 0),
    precioVenta: venta.valor_final,
    utilidadBruta: venta.valor_final - produccion.reduce((sum, item) => sum + item.costo_total, 0),
    margenGanancia: 0
  };

  calculos.margenGanancia = calculos.precioVenta > 0
    ? (calculos.utilidadBruta / calculos.precioVenta) * 100
    : 0;

  const handleSave = () => {
    const costingData = {
      venta,
      produccion,
      costo_total_produccion: calculos.costoTotalProduccion,
      utilidad_bruta: calculos.utilidadBruta,
      margen_ganancia: calculos.margenGanancia
    };
    
    onSave(costingData);
    toast.success('Costeo guardado');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  return (
    <div className="space-y-8">
      <div className="bg-amber-50 border border-amber-200 p-4 rounded-none">
        <div className="flex items-start gap-2">
          <div className="text-amber-600 mt-1">⚠️</div>
          <div>
            <p className="text-sm font-medium text-amber-900 mb-1">
              INFORMACIÓN DE USO INTERNO
            </p>
            <p className="text-xs text-amber-700">
              Esta sección es solo para control interno de la empresa. No se muestra al cliente ni en el PDF de cliente.
            </p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-serif text-xl text-black mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5" />
          Registro de Venta
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              Precio de Venta Total *
            </label>
            <input
              type="number"
              value={venta.precio_venta_total}
              onChange={(e) => setVenta({ ...venta, precio_venta_total: parseFloat(e.target.value) || 0 })}
              className="w-full h-10 border border-input bg-white px-3 text-sm focus:outline-none focus:border-black"
              placeholder="0"
              data-testid="precio-venta-total"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              Tipo de Venta
            </label>
            <select
              value={venta.tipo_venta}
              onChange={(e) => setVenta({ ...venta, tipo_venta: e.target.value })}
              className="w-full h-10 border border-input bg-white px-3 text-sm focus:outline-none focus:border-black"
              data-testid="tipo-venta"
            >
              <option value="">Seleccionar...</option>
              <option value="Traje Completo">Traje Completo</option>
              <option value="Blazer">Blazer</option>
              <option value="Pantalón">Pantalón</option>
              <option value="Camisa">Camisa</option>
              <option value="Chaleco">Chaleco</option>
              <option value="Chaqueta/Gabán">Chaqueta/Gabán</option>
              <option value="Otro">Otro</option>
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              Descuento Aplicado
            </label>
            <input
              type="number"
              value={venta.descuento}
              onChange={(e) => setVenta({ ...venta, descuento: parseFloat(e.target.value) || 0 })}
              className="w-full h-10 border border-input bg-white px-3 text-sm focus:outline-none focus:border-black"
              placeholder="0"
              data-testid="descuento"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              Valor Final (Calculado)
            </label>
            <div className="h-10 border border-zinc-300 bg-zinc-100 px-3 text-sm flex items-center font-medium">
              {formatCurrency(venta.valor_final)}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              Forma de Pago
            </label>
            <select
              value={venta.forma_pago}
              onChange={(e) => setVenta({ ...venta, forma_pago: e.target.value })}
              className="w-full h-10 border border-input bg-white px-3 text-sm focus:outline-none focus:border-black"
              data-testid="forma-pago"
            >
              <option value="">Seleccionar...</option>
              <option value="Efectivo">Efectivo</option>
              <option value="Transferencia">Transferencia</option>
              <option value="Tarjeta">Tarjeta</option>
              <option value="Cheque">Cheque</option>
              <option value="Mixto">Mixto</option>
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              Anticipo Recibido
            </label>
            <input
              type="number"
              value={venta.anticipo}
              onChange={(e) => setVenta({ ...venta, anticipo: parseFloat(e.target.value) || 0 })}
              className="w-full h-10 border border-input bg-white px-3 text-sm focus:outline-none focus:border-black"
              placeholder="0"
              data-testid="anticipo"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              Saldo Pendiente (Calculado)
            </label>
            <div className="h-10 border border-zinc-300 bg-zinc-100 px-3 text-sm flex items-center font-medium">
              {formatCurrency(venta.saldo_pendiente)}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl text-black flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Costeo de Producción
          </h3>
          <button
            type="button"
            onClick={agregarItem}
            className="flex items-center gap-2 px-4 h-10 bg-black text-white hover:bg-zinc-800 rounded-none text-xs uppercase tracking-widest transition-all"
            data-testid="add-cost-item"
          >
            <Plus className="w-4 h-4" />
            Agregar Item
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border border-border">
            <thead className="bg-zinc-50">
              <tr>
                <th className="border border-border px-3 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  Concepto
                </th>
                <th className="border border-border px-3 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  Proveedor
                </th>
                <th className="border border-border px-3 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  Cantidad
                </th>
                <th className="border border-border px-3 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  Costo Unitario
                </th>
                <th className="border border-border px-3 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  Costo Total
                </th>
                <th className="border border-border px-3 py-2 text-center text-xs uppercase tracking-wider text-muted-foreground">
                  Acción
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {produccion.length === 0 ? (
                <tr>
                  <td colSpan="6" className="border border-border px-3 py-8 text-center text-sm text-muted-foreground">
                    No hay items de costo. Haz clic en "Agregar Item" para comenzar.
                  </td>
                </tr>
              ) : (
                produccion.map((item) => (
                  <tr key={item.id} data-testid={`cost-item-${item.id}`}>
                    <td className="border border-border px-3 py-2">
                      <input
                        type="text"
                        value={item.concepto}
                        onChange={(e) => actualizarItem(item.id, 'concepto', e.target.value)}
                        placeholder="Ej: Tela, Botones..."
                        className="w-full h-8 border border-input bg-white px-2 text-sm focus:outline-none focus:border-black"
                        data-testid={`concepto-${item.id}`}
                      />
                    </td>
                    <td className="border border-border px-3 py-2">
                      <input
                        type="text"
                        value={item.proveedor}
                        onChange={(e) => actualizarItem(item.id, 'proveedor', e.target.value)}
                        placeholder="Nombre del proveedor"
                        className="w-full h-8 border border-input bg-white px-2 text-sm focus:outline-none focus:border-black"
                        data-testid={`proveedor-${item.id}`}
                      />
                    </td>
                    <td className="border border-border px-3 py-2">
                      <input
                        type="number"
                        value={item.cantidad}
                        onChange={(e) => actualizarItem(item.id, 'cantidad', parseFloat(e.target.value) || 0)}
                        className="w-full h-8 border border-input bg-white px-2 text-sm focus:outline-none focus:border-black"
                        data-testid={`cantidad-${item.id}`}
                      />
                    </td>
                    <td className="border border-border px-3 py-2">
                      <input
                        type="number"
                        value={item.costo_unitario}
                        onChange={(e) => actualizarItem(item.id, 'costo_unitario', parseFloat(e.target.value) || 0)}
                        className="w-full h-8 border border-input bg-white px-2 text-sm focus:outline-none focus:border-black"
                        data-testid={`costo-unitario-${item.id}`}
                      />
                    </td>
                    <td className="border border-border px-3 py-2 bg-zinc-50 font-medium text-sm">
                      {formatCurrency(item.costo_total)}
                    </td>
                    <td className="border border-border px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => eliminarItem(item.id)}
                        className="text-destructive hover:text-destructive/80 transition-colors"
                        data-testid={`delete-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border-t border-border pt-8">
        <h3 className="font-serif text-xl text-black mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Análisis Automático
        </h3>

        {calculos.margenGanancia < 70 && calculos.precioVenta > 0 && (
          <div className="bg-red-50 border-2 border-red-500 p-4 rounded-none mb-6">
            <div className="flex items-start gap-2">
              <div className="text-red-600 text-2xl">⚠️</div>
              <div>
                <p className="text-sm font-bold text-red-900 mb-1">
                  ALERTA: MARGEN DE GANANCIA BAJO
                </p>
                <p className="text-xs text-red-700">
                  El margen de ganancia es del {calculos.margenGanancia.toFixed(2)}%, por debajo del 70% recomendado.
                  Revisa los costos de producción o ajusta el precio de venta.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-border p-6">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Costo Total Producción
            </p>
            <p className="text-2xl font-serif font-medium text-black">
              {formatCurrency(calculos.costoTotalProduccion)}
            </p>
          </div>

          <div className="bg-white border border-border p-6">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Precio de Venta
            </p>
            <p className="text-2xl font-serif font-medium text-black">
              {formatCurrency(calculos.precioVenta)}
            </p>
          </div>

          <div className={`border p-6 ${calculos.utilidadBruta >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Utilidad Bruta
            </p>
            <p className={`text-2xl font-serif font-medium ${calculos.utilidadBruta >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {formatCurrency(calculos.utilidadBruta)}
            </p>
          </div>

          <div className={`border p-6 ${calculos.margenGanancia >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
              Margen de Ganancia
            </p>
            <p className={`text-2xl font-serif font-medium ${calculos.margenGanancia >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {calculos.margenGanancia.toFixed(2)}%
            </p>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={handleSave}
        className="w-full bg-black text-white hover:bg-zinc-800 rounded-none h-12 uppercase tracking-widest text-xs font-medium transition-all"
        data-testid="save-costing"
      >
        Guardar Costeo
      </button>
    </div>
  );
};
